import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Filter, Trash2, Pencil, Download, Printer, Share2, Eraser,
  TrendingUp, Package, CheckCircle2, Clock, XCircle, X, Save, Plus,
  CheckSquare, Square, AlertTriangle
} from "lucide-react";
import api from "@/api";
import TopBar from "@/components/TopBar";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

// Tier 4A (Feb 2026): Single-screen Sales control for Super Admin / Management.
// Filters → Cards → Table → Edit/Delete → PDF/Print/Share. No clutter, fully Tamil-aware.
const STATUS_BADGES = {
  pending:        { ta: "காத்திருக்கிறது",  en: "Pending",       cls: "bg-amber-50 text-amber-700" },
  assigned:       { ta: "ஒதுக்கப்பட்டது",   en: "Assigned",      cls: "bg-indigo-50 text-indigo-700" },
  out_for_delivery:{ta: "வழியில்",         en: "Out for delivery", cls: "bg-blue-50 text-blue-700" },
  delivered:      { ta: "டெலிவரி",         en: "Delivered",     cls: "bg-green-50 text-green-700" },
  cancelled:      { ta: "ரத்து",            en: "Cancelled",     cls: "bg-red-50 text-red-700" },
};
const STATUS_KEYS = Object.keys(STATUS_BADGES);

const REASON_LABELS = {
  shop_closed:        { en: "Shop Closed",      ta: "கடை மூடப்பட்டது" },
  already_has_stock:  { en: "Has Stock",         ta: "ஸ்டாக் இருக்கு" },
  other_company:      { en: "Other Company",     ta: "வேற கம்பெனி" },        // legacy — display only
  owner_absent:       { en: "Owner Absent",      ta: "உரிமையாளர் இல்ல" },
  asking_credit:      { en: "Asking Credit",     ta: "கடன் கேக்குறார்" },
  price_issue:        { en: "Price Issue",       ta: "விலை பிரச்சினை" },     // legacy — display only
  other:              { en: "Reason not listed", ta: "காரணம் பட்டியலில் இல்ல" },
};
// Codes shown in NEW-entry pickers (legacy ones omitted per Apr 2026 cleanup)
const REASON_KEYS = ["shop_closed", "already_has_stock", "owner_absent", "asking_credit", "other"];

const KPI = ({ icon: Icon, label, value, color }) => (
  <div className="bg-white rounded-2xl p-5 soft-shadow flex items-center gap-4">
    <div className={`w-12 h-12 rounded-2xl ${color} grid place-items-center shrink-0`}>
      <Icon className="w-6 h-6 text-white" />
    </div>
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-wider text-[#86868B] font-bold truncate">{label}</div>
      <div className="heading-font text-3xl font-bold text-[#1D1D1F]">{value}</div>
    </div>
  </div>
);

export default function AdminSales() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const today = new Date().toISOString().slice(0, 10);

  // Prefilters from URL (e.g. ?status=delivered, ?status=pending, ?sort=boxes, ?view=nosale)
  const initialStatus = searchParams.get("status") || "";
  const initialSort = searchParams.get("sort") || "";
  const initialView = searchParams.get("view") === "nosale" ? "nosale" : "orders";

  // Tab: "orders" (default) | "nosale"
  const [view, setView] = useState(initialView);

  // Filters (default: today only)
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  const [salesmanId, setSalesmanId] = useState("");
  const [managerId, setManagerId] = useState("");
  const [productId, setProductId] = useState("");
  const [districtId, setDistrictId] = useState("");
  const [statusFilter, setStatusFilter] = useState(initialStatus);
  const [sortDesc, setSortDesc] = useState(true); // by date
  const [sortByBoxes, setSortByBoxes] = useState(initialSort === "boxes");

  // Selection (Tier 4B): super_admin only
  const [selected, setSelected] = useState(new Set());
  const clearSelection = () => setSelected(new Set());
  const toggleOne = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  // Lookups
  const [salesmen, setSalesmen] = useState([]);
  const [managers, setManagers] = useState([]);
  const [products, setProducts] = useState([]);
  const [districts, setDistricts] = useState([]);

  // Data
  const [orders, setOrders] = useState([]);
  const [noSales, setNoSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);            // order being edited
  const [editingNoSale, setEditingNoSale] = useState(null); // no-sale being edited

  useEffect(() => {
    Promise.all([
      api.get("/users", { params: { role: "salesman" } }).then(r => r.data || []).catch(() => []),
      api.get("/users", { params: { role: "manager" } }).then(r => r.data || []).catch(() => []),
      api.get("/products").then(r => r.data || []).catch(() => []),
      api.get("/districts").then(r => r.data || []).catch(() => []),
    ]).then(([s, m, p, d]) => { setSalesmen(s); setManagers(m); setProducts(p); setDistricts(d); });
  }, []);

  const fetchData = () => {
    setLoading(true);
    const params = {};
    if (fromDate) params.start_date = fromDate;
    if (toDate) params.end_date = toDate;
    if (salesmanId) params.salesman_id = salesmanId;
    if (managerId) params.manager_id = managerId;
    if (productId) params.product_id = productId;
    if (districtId) params.district_id = districtId;
    if (statusFilter) params.status = statusFilter;
    Promise.all([
      api.get("/orders", { params }).then(r => r.data || []),
      api.get("/no-sales", {
        params: {
          ...(salesmanId ? { salesman_id: salesmanId } : {}),
          ...(fromDate ? { date_from: fromDate } : {}),
          ...(toDate ? { date_to: toDate } : {}),
        }
      }).then(r => r.data || []).catch(() => []),
    ]).then(([o, n]) => { setOrders(o); setNoSales(n); }).finally(() => setLoading(false));
  };
  useEffect(fetchData, [fromDate, toDate, salesmanId, managerId, productId, districtId, statusFilter]);

  // Clear selection any time the active view or filters change
  useEffect(() => { clearSelection(); }, [view, fromDate, toDate, salesmanId, managerId, productId, districtId, statusFilter]);

  const clearFilters = () => {
    setFromDate(today); setToDate(today); setSalesmanId(""); setManagerId(""); setProductId(""); setDistrictId(""); setStatusFilter("");
    toast(lang === "ta" ? "வடிகட்டிகள் அழிக்கப்பட்டன" : "Filters cleared");
  };

  const askDelete = (o) => {
    const msg = lang === "ta" ? `இந்த ஆர்டர் நீக்கவா?\n${o.shop_name} • ${o.total_units} பெட்டிகள்` : `Are you sure you want to delete this record?\n${o.shop_name} • ${o.total_units} boxes`;
    if (!window.confirm(msg)) return;
    api.delete(`/admin/orders/${o.id}`)
      .then(() => { toast.success(lang === "ta" ? "நீக்கப்பட்டது" : "Deleted"); fetchData(); })
      .catch(e => toast.error(e?.response?.data?.detail || "Delete failed"));
  };

  const askClear = (o) => {
    const msg = lang === "ta" ? `இந்த ஆர்டர் ரீசெட் ஆகுமா?\n${o.shop_name}\n(பெட்டிகள் = 0, நிலை = pending)` : `Reset this order?\n${o.shop_name}\n(boxes will become 0, status = pending. Record kept.)`;
    if (!window.confirm(msg)) return;
    api.post(`/admin/orders/${o.id}/clear`)
      .then(() => { toast.success(lang === "ta" ? "ரீசெட் ஆனது" : "Cleared"); fetchData(); })
      .catch(e => toast.error(e?.response?.data?.detail || "Clear failed"));
  };

  const askDeleteNoSale = (n) => {
    const msg = lang === "ta" ? `இந்த 'வாங்கல' பதிவு நீக்கவா?\n${n.shop_name}` : `Delete this 'Not Bought' record?\n${n.shop_name}`;
    if (!window.confirm(msg)) return;
    api.delete(`/admin/no-sales/${n.id}`)
      .then(() => { toast.success(lang === "ta" ? "நீக்கப்பட்டது" : "Deleted"); fetchData(); })
      .catch(e => toast.error(e?.response?.data?.detail || "Delete failed"));
  };

  const bulkDelete = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    const label = view === "orders" ? (lang === "ta" ? "ஆர்டர்கள்" : "orders") : (lang === "ta" ? "பதிவுகள்" : "no-sale records");
    const msg = lang === "ta"
      ? `${ids.length} ${label} நீக்கவா? இந்த செயல் பதிவாகும்.`
      : `Delete ${ids.length} selected ${label}? This action will be logged.`;
    if (!window.confirm(msg)) return;
    try {
      const url = view === "orders" ? "/admin/orders/bulk-delete" : "/admin/no-sales/bulk-delete";
      const res = await api.post(url, { ids, reason: "admin bulk delete" });
      toast.success(lang === "ta" ? `${res.data.deleted || ids.length} நீக்கப்பட்டது` : `Deleted ${res.data.deleted || ids.length}`);
      clearSelection();
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk delete failed");
    }
  };

  const bulkClear = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    const msg = lang === "ta"
      ? `${ids.length} ஆர்டர்கள் ரீசெட் ஆகுமா?\n(பெட்டிகள் = 0, நிலை = pending. பதிவு சேமிக்கப்படும்.)`
      : `Reset ${ids.length} selected orders?\n(boxes → 0, status → pending. Records are preserved.)`;
    if (!window.confirm(msg)) return;
    try {
      const res = await api.post("/admin/orders/bulk-clear", { ids, reason: "admin bulk clear" });
      toast.success(lang === "ta" ? `${res.data.cleared || ids.length} ரீசெட் ஆனது` : `Cleared ${res.data.cleared || ids.length}`);
      clearSelection();
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk clear failed");
    }
  };

  // Aggregations
  const stats = useMemo(() => {
    const delivered = orders.filter(o => o.status === "delivered").length;
    const pending = orders.filter(o => o.status === "pending" || o.status === "assigned" || o.status === "out_for_delivery").length;
    const totalBoxes = orders.reduce((s, o) => s + (o.total_units || 0), 0);
    return { totalBoxes, totalOrders: orders.length, delivered, pending, noSale: noSales.length };
  }, [orders, noSales]);

  // Lookup maps for Tamil display
  const salesmanMap = useMemo(() => Object.fromEntries(salesmen.map(s => [s.id, s])), [salesmen]);
  const districtMap = useMemo(() => Object.fromEntries(districts.map(d => [d.id, d])), [districts]);

  const dispName = (it, fallback = "—") => {
    if (!it) return fallback;
    if (lang === "ta" && it.name_ta) return it.name_ta;
    return it.name || it.name_en || fallback;
  };
  const productLabel = (item) => {
    if (lang === "ta" && item?.name_ta) return item.name_ta;
    return item?.name_en || item?.name || "—";
  };

  // Sorted rows
  const tableRows = useMemo(() => {
    const arr = [...orders];
    if (sortByBoxes) {
      arr.sort((a, b) => (b.total_units || 0) - (a.total_units || 0));
    } else {
      arr.sort((a, b) => {
        const av = a.delivered_at || a.created_at || "";
        const bv = b.delivered_at || b.created_at || "";
        return sortDesc ? bv.localeCompare(av) : av.localeCompare(bv);
      });
    }
    return arr;
  }, [orders, sortDesc, sortByBoxes]);

  const openPrint = async () => {
    const params = { lang };
    if (fromDate) params.start_date = fromDate;
    if (toDate) params.end_date = toDate;
    if (salesmanId) params.salesman_id = salesmanId;
    if (managerId) params.manager_id = managerId;
    if (productId) params.product_id = productId;
    if (districtId) params.district_id = districtId;
    if (statusFilter) params.status = statusFilter;
    try {
      const res = await api.get("/reports/orders.html", { params, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/html" }));
      const w = window.open(url, "_blank");
      if (!w) toast.error(lang === "ta" ? "பாப்-அப் தடுக்கப்பட்டது" : "Pop-up blocked");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      return url;
    } catch (e) {
      toast.error(lang === "ta" ? "பதிவேற்ற முடியவில்ல" : "Print failed");
      return null;
    }
  };

  const shareWhatsApp = () => {
    const range = (fromDate === toDate) ? fromDate : `${fromDate} → ${toDate}`;
    const summary = lang === "ta"
      ? `🧾 GT Royal விற்பனை அறிக்கை\n📅 ${range}\n📦 பெட்டிகள்: ${stats.totalBoxes}\n📋 ஆர்டர்கள்: ${stats.totalOrders}\n✅ டெலிவரி: ${stats.delivered}\n⏳ பெண்டிங்: ${stats.pending}\n❌ வாங்கல: ${stats.noSale}`
      : `🧾 GT Royal Sales Report\n📅 ${range}\n📦 Boxes: ${stats.totalBoxes}\n📋 Orders: ${stats.totalOrders}\n✅ Delivered: ${stats.delivered}\n⏳ Pending: ${stats.pending}\n❌ No-Sale: ${stats.noSale}`;
    const url = `https://wa.me/?text=${encodeURIComponent(summary)}`;
    window.open(url, "_blank");
    toast.success(lang === "ta" ? "WhatsApp திறக்கப்பட்டது" : "WhatsApp opened");
  };

  const filteredManagers = useMemo(() => {
    if (!districtId) return managers;
    return managers.filter(m => m.district_id === districtId);
  }, [managers, districtId]);
  const filteredSalesmen = useMemo(() => {
    let arr = salesmen;
    if (districtId) arr = arr.filter(s => s.district_id === districtId);
    return arr;
  }, [salesmen, districtId]);

  return (
    <div className="min-h-screen bg-[#F5F5F7] page-slide-in pb-32">
      <TopBar title={lang === "ta" ? "விற்பனை" : "Sales"} onBack={() => nav("/admin")} />
      <div className="max-w-7xl mx-auto p-4 sm:p-6 space-y-5 fade-up">

        {/* VIEW TABS — Orders vs Not Bought */}
        <div className="flex gap-2 bg-white rounded-2xl p-1.5 soft-shadow w-fit" data-testid="sales-view-tabs">
          <button data-testid="sales-tab-orders" onClick={() => setView("orders")}
            className={`px-4 py-2 rounded-xl font-bold text-sm transition ${view === "orders" ? "bg-[#0055FF] text-white" : "text-[#1D1D1F] hover:bg-gray-50"}`}>
            {lang === "ta" ? "ஆர்டர்கள்" : "Orders"}
          </button>
          <button data-testid="sales-tab-nosale" onClick={() => setView("nosale")}
            className={`px-4 py-2 rounded-xl font-bold text-sm transition ${view === "nosale" ? "bg-[#FF3B30] text-white" : "text-[#1D1D1F] hover:bg-gray-50"}`}>
            {lang === "ta" ? "வாங்கல" : "Not Bought"}
          </button>
        </div>

        {/* FILTERS */}
        <div className="bg-white rounded-2xl soft-shadow p-4 sm:p-5 space-y-3" data-testid="sales-filters">
          <div className="flex items-center gap-2 text-[#1D1D1F] font-bold heading-font">
            <Filter className="w-4 h-4" /> {t("filters") || "Filters"}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <FieldDate label={lang === "ta" ? "முதல் தேதி" : "From"} value={fromDate} onChange={setFromDate} max={toDate} testid="sales-from" />
            <FieldDate label={lang === "ta" ? "கடைசி தேதி" : "To"} value={toDate} onChange={setToDate} min={fromDate} testid="sales-to" />
            <FieldSelect label={lang === "ta" ? "மாவட்டம்" : "District"} value={districtId} onChange={setDistrictId} testid="sales-district"
              options={[{ id: "", name: t("all") || "All" }, ...districts]} dispName={dispName} />
            <FieldSelect label={lang === "ta" ? "மேனேஜர்" : "Manager"} value={managerId} onChange={(v) => { setManagerId(v); if (v) setSalesmanId(""); }} testid="sales-manager"
              options={[{ id: "", name: t("all") || "All" }, ...filteredManagers]} dispName={dispName} />
            <FieldSelect label={lang === "ta" ? "சேல்ஸ்மேன்" : "Salesman"} value={salesmanId} onChange={(v) => { setSalesmanId(v); if (v) setManagerId(""); }} testid="sales-salesman"
              options={[{ id: "", name: t("all") || "All" }, ...filteredSalesmen]} dispName={dispName} />
            <FieldSelect label={lang === "ta" ? "பொருள்" : "Product"} value={productId} onChange={setProductId} testid="sales-product"
              options={[{ id: "", name: t("all") || "All" }, ...products.map(p => ({ id: p.id, name: p.name_en || p.name, name_ta: p.name_ta }))]} dispName={dispName} />
            <FieldSelect label={t("status")} value={statusFilter} onChange={setStatusFilter} testid="sales-status"
              options={[{ id: "", name: t("all") || "All" }, ...STATUS_KEYS.map(k => ({ id: k, name: STATUS_BADGES[k].en, name_ta: STATUS_BADGES[k].ta }))]} dispName={dispName} />
          </div>
          <div className="flex flex-wrap gap-2 pt-1">
            <button onClick={clearFilters} data-testid="sales-clear-btn"
              className="px-4 py-2 rounded-xl bg-gray-100 hover:bg-gray-200 text-[#1D1D1F] font-bold text-sm">
              {t("clear_filters") || "Clear Filters"}
            </button>
            <button onClick={() => setSortByBoxes(!sortByBoxes)} data-testid="sales-sort-btn"
              className="px-4 py-2 rounded-xl bg-gray-100 hover:bg-gray-200 text-[#1D1D1F] font-bold text-sm">
              {sortByBoxes ? (lang === "ta" ? "சமீபத்திய முதலில்" : "Latest first") : (lang === "ta" ? "பெட்டிகள் கீழ்" : "Boxes ↓")}
            </button>
          </div>
        </div>

        {/* SUMMARY CARDS */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="sales-cards">
          <KPI icon={Package} label={t("boxes_sold") || "Total Boxes"} value={stats.totalBoxes} color="bg-[#0055FF]" />
          <KPI icon={TrendingUp} label={t("orders") || "Orders"} value={stats.totalOrders} color="bg-[#5856D6]" />
          <KPI icon={CheckCircle2} label={lang === "ta" ? "டெலிவரி" : "Delivered"} value={stats.delivered} color="bg-[#34C759]" />
          <KPI icon={Clock} label={t("pending") || "Pending"} value={stats.pending} color="bg-[#FF9500]" />
          <KPI icon={XCircle} label={t("not_bought") || "No-Sale"} value={stats.noSale} color="bg-[#FF3B30]" />
        </div>

        {/* TABLE */}
        {view === "orders" ? (
        <div className="bg-white rounded-2xl soft-shadow overflow-hidden" data-testid="sales-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50/60">
                <tr className="text-left text-[#86868B] uppercase tracking-wider text-xs">
                  {isSuperAdmin && (
                    <th className="px-3 py-3 w-10">
                      <button data-testid="sales-select-all"
                        onClick={() => {
                          if (selected.size === tableRows.length) clearSelection();
                          else setSelected(new Set(tableRows.map(r => r.id)));
                        }}
                        className="text-[#86868B] hover:text-[#0055FF]">
                        {selected.size > 0 && selected.size === tableRows.length ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                      </button>
                    </th>
                  )}
                  <th className="px-4 py-3">{lang === "ta" ? "தேதி" : "Date"}</th>
                  <th className="px-4 py-3">{lang === "ta" ? "கடை" : "Store"}</th>
                  <th className="px-4 py-3">{lang === "ta" ? "மாவட்டம்" : "District"}</th>
                  <th className="px-4 py-3">{lang === "ta" ? "பொருள்" : "Product"}</th>
                  <th className="px-4 py-3">{t("boxes") || "Boxes"}</th>
                  <th className="px-4 py-3">{lang === "ta" ? "சேல்ஸ்மேன்" : "Salesman"}</th>
                  <th className="px-4 py-3">{t("status")}</th>
                  <th className="px-4 py-3 text-right">{lang === "ta" ? "செயல்கள்" : "Actions"}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <tr><td colSpan={isSuperAdmin ? "9" : "8"} className="px-4 py-12 text-center text-[#86868B]">{t("loading")}</td></tr>
                ) : tableRows.length === 0 ? (
                  <tr><td colSpan={isSuperAdmin ? "9" : "8"} className="px-4 py-12 text-center text-[#86868B]" data-testid="sales-empty">
                    {lang === "ta" ? "தரவு இல்ல" : "No data for selected filters"}
                  </td></tr>
                ) : tableRows.map(o => {
                  const dt = (o.delivered_at || o.created_at || "").slice(0, 10);
                  const sm = salesmanMap[o.assigned_salesman_id];
                  const dist = districtMap[o.district_id];
                  const status = STATUS_BADGES[o.status] || STATUS_BADGES.pending;
                  const items = o.items || [];
                  const isSel = selected.has(o.id);
                  return (
                    <tr key={o.id} className={`hover:bg-gray-50/40 ${isSel ? "bg-blue-50/40" : ""}`} data-testid={`sales-row-${o.id}`}>
                      {isSuperAdmin && (
                        <td className="px-3 py-3">
                          <button onClick={() => toggleOne(o.id)} data-testid={`sales-checkbox-${o.id}`}
                            className={`p-1 rounded ${isSel ? "text-[#0055FF]" : "text-[#86868B] hover:text-[#0055FF]"}`}>
                            {isSel ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                          </button>
                        </td>
                      )}
                      <td className="px-4 py-3 whitespace-nowrap font-mono text-xs">{dt}</td>
                      <td className="px-4 py-3 font-semibold heading-font">
                        {(lang === "ta" && o.shop_name_ta) ? o.shop_name_ta : o.shop_name || "—"}
                      </td>
                      <td className="px-4 py-3 text-[#86868B]">{dispName(dist)}</td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col gap-0.5">
                          {items.slice(0, 2).map((it, i) => (
                            <span key={i} className="truncate max-w-[200px]">{productLabel(it)} <span className="text-[#86868B] text-xs">×{it.quantity}</span></span>
                          ))}
                          {items.length > 2 && <span className="text-xs text-[#86868B]">+{items.length - 2}…</span>}
                        </div>
                      </td>
                      <td className="px-4 py-3 font-bold">{o.total_units || 0}</td>
                      <td className="px-4 py-3">{(lang === "ta" && o.assigned_salesman_name_ta) ? o.assigned_salesman_name_ta : (sm ? dispName(sm) : (o.assigned_salesman_name || "—"))}</td>
                      <td className="px-4 py-3"><span className={`pill ${status.cls}`}>{lang === "ta" ? status.ta : status.en}</span></td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1 justify-end">
                          <button onClick={() => setEditing(o)} data-testid={`sales-edit-${o.id}`}
                            className="p-2 rounded-lg hover:bg-blue-50 text-[#0055FF]" title={lang === "ta" ? "திருத்து" : "Edit"}>
                            <Pencil className="w-4 h-4" />
                          </button>
                          {isSuperAdmin && (
                            <button onClick={() => askClear(o)} data-testid={`sales-clear-row-${o.id}`}
                              className="p-2 rounded-lg hover:bg-amber-50 text-[#FF9500]" title={lang === "ta" ? "ரீசெட்" : "Clear (reset)"}>
                              <Eraser className="w-4 h-4" />
                            </button>
                          )}
                          {isSuperAdmin && (
                            <button onClick={() => askDelete(o)} data-testid={`sales-delete-${o.id}`}
                              className="p-2 rounded-lg hover:bg-red-50 text-[#FF3B30]" title={lang === "ta" ? "நீக்கு" : "Delete"}>
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        ) : (
        /* NOT-BOUGHT (no_sales) TABLE */
        <div className="bg-white rounded-2xl soft-shadow overflow-hidden" data-testid="sales-nosale-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50/60">
                <tr className="text-left text-[#86868B] uppercase tracking-wider text-xs">
                  {isSuperAdmin && (
                    <th className="px-3 py-3 w-10">
                      <button data-testid="nosale-select-all"
                        onClick={() => {
                          if (selected.size === noSales.length) clearSelection();
                          else setSelected(new Set(noSales.map(r => r.id)));
                        }}
                        className="text-[#86868B] hover:text-[#FF3B30]">
                        {selected.size > 0 && selected.size === noSales.length ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                      </button>
                    </th>
                  )}
                  <th className="px-4 py-3">{lang === "ta" ? "தேதி" : "Date"}</th>
                  <th className="px-4 py-3">{lang === "ta" ? "கடை" : "Store"}</th>
                  <th className="px-4 py-3">{lang === "ta" ? "தொலைபேசி" : "Phone"}</th>
                  <th className="px-4 py-3">{lang === "ta" ? "காரணம்" : "Reason"}</th>
                  <th className="px-4 py-3">{lang === "ta" ? "சேல்ஸ்மேன்" : "Salesman"}</th>
                  <th className="px-4 py-3 text-right">{lang === "ta" ? "செயல்கள்" : "Actions"}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <tr><td colSpan={isSuperAdmin ? "7" : "6"} className="px-4 py-12 text-center text-[#86868B]">{t("loading")}</td></tr>
                ) : noSales.length === 0 ? (
                  <tr><td colSpan={isSuperAdmin ? "7" : "6"} className="px-4 py-12 text-center text-[#86868B]" data-testid="nosale-empty">
                    {lang === "ta" ? "வாங்கல பதிவு இல்ல" : "No 'Not Bought' records"}
                  </td></tr>
                ) : noSales.map(n => {
                  const sm = salesmanMap[n.salesman_id];
                  const r = REASON_LABELS[n.reason_code] || REASON_LABELS.other;
                  const isSel = selected.has(n.id);
                  return (
                    <tr key={n.id} className={`hover:bg-gray-50/40 ${isSel ? "bg-red-50/40" : ""}`} data-testid={`nosale-row-${n.id}`}>
                      {isSuperAdmin && (
                        <td className="px-3 py-3">
                          <button onClick={() => toggleOne(n.id)} data-testid={`nosale-checkbox-${n.id}`}
                            className={`p-1 rounded ${isSel ? "text-[#FF3B30]" : "text-[#86868B] hover:text-[#FF3B30]"}`}>
                            {isSel ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                          </button>
                        </td>
                      )}
                      <td className="px-4 py-3 whitespace-nowrap font-mono text-xs">{(n.date || n.created_at || "").slice(0, 10)}</td>
                      <td className="px-4 py-3 font-semibold heading-font">{n.shop_name || "—"}</td>
                      <td className="px-4 py-3 text-[#86868B] font-mono text-xs">{n.phone || "—"}</td>
                      <td className="px-4 py-3">
                        <span className="pill bg-red-50 text-red-700">{lang === "ta" ? r.ta : r.en}</span>
                        {n.reason_code === "other" && n.custom_reason && (
                          <div className="text-xs text-[#86868B] mt-1 italic">"{n.custom_reason}"</div>
                        )}
                      </td>
                      <td className="px-4 py-3">{sm ? dispName(sm) : "—"}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1 justify-end">
                          {isSuperAdmin && (
                            <button onClick={() => setEditingNoSale(n)} data-testid={`nosale-edit-${n.id}`}
                              className="p-2 rounded-lg hover:bg-blue-50 text-[#0055FF]" title={lang === "ta" ? "திருத்து" : "Edit"}>
                              <Pencil className="w-4 h-4" />
                            </button>
                          )}
                          {isSuperAdmin && (
                            <button onClick={() => askDeleteNoSale(n)} data-testid={`nosale-delete-${n.id}`}
                              className="p-2 rounded-lg hover:bg-red-50 text-[#FF3B30]" title={lang === "ta" ? "நீக்கு" : "Delete"}>
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        )}
      </div>

      {/* BULK ACTION BAR — only super_admin, only when ≥1 selected */}
      {isSuperAdmin && selected.size > 0 && (
        <div className="fixed bottom-20 left-0 right-0 z-40 px-4" data-testid="bulk-bar">
          <div className="max-w-3xl mx-auto bg-[#0A1A3F] text-white rounded-2xl shadow-2xl p-3 flex items-center gap-3 fade-up">
            <div className="flex items-center gap-2 flex-1">
              <AlertTriangle className="w-5 h-5 text-amber-300" />
              <div>
                <div className="font-bold">{selected.size} {lang === "ta" ? "தேர்ந்தெடுக்கப்பட்டது" : "selected"}</div>
                <div className="text-xs opacity-70">{lang === "ta" ? "செயல்: அனைத்தும் பதிவாகும்" : "Action will be logged."}</div>
              </div>
            </div>
            {view === "orders" && (
              <button onClick={bulkClear} data-testid="bulk-clear-btn"
                className="bg-[#FF9500] hover:bg-[#cc7700] text-white rounded-xl px-4 py-2 font-bold text-sm flex items-center gap-1.5">
                <Eraser className="w-4 h-4" /> {lang === "ta" ? "ரீசெட்" : "Bulk Clear"}
              </button>
            )}
            <button onClick={bulkDelete} data-testid="bulk-delete-btn"
              className="bg-[#FF3B30] hover:bg-[#cc2f25] text-white rounded-xl px-4 py-2 font-bold text-sm flex items-center gap-1.5">
              <Trash2 className="w-4 h-4" /> {lang === "ta" ? "நீக்கு" : "Bulk Delete"}
            </button>
            <button onClick={clearSelection} data-testid="bulk-cancel-btn"
              className="bg-white/10 hover:bg-white/20 text-white rounded-xl px-3 py-2 text-sm">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Sticky bottom action bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur border-t border-gray-200 z-30">
        <div className="max-w-7xl mx-auto p-3 flex flex-wrap gap-2 justify-end">
          <button onClick={openPrint} data-testid="sales-pdf-btn"
            className="bg-[#0055FF] hover:bg-[#0044CC] text-white rounded-xl px-5 py-3 font-bold text-sm flex items-center gap-2">
            <Download className="w-4 h-4" /> {lang === "ta" ? "PDF / அச்சிடு" : "PDF / Print"}
          </button>
          <button onClick={openPrint} data-testid="sales-print-btn"
            className="bg-white hover:bg-gray-50 text-[#1D1D1F] border border-gray-300 rounded-xl px-5 py-3 font-bold text-sm flex items-center gap-2">
            <Printer className="w-4 h-4" /> {t("print") || "Print"}
          </button>
          <button onClick={shareWhatsApp} data-testid="sales-share-btn"
            className="bg-[#25D366] hover:bg-[#1DA851] text-white rounded-xl px-5 py-3 font-bold text-sm flex items-center gap-2">
            <Share2 className="w-4 h-4" /> WhatsApp
          </button>
        </div>
      </div>

      {editing && (
        <EditOrderModal
          order={editing}
          products={products}
          salesmen={salesmen}
          managers={managers}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); fetchData(); }}
          lang={lang}
        />
      )}

      {editingNoSale && (
        <EditNoSaleModal
          record={editingNoSale}
          onClose={() => setEditingNoSale(null)}
          onSaved={() => { setEditingNoSale(null); fetchData(); }}
          lang={lang}
        />
      )}
    </div>
  );
}

const FieldDate = ({ label, value, onChange, min, max, testid }) => (
  <div>
    <label className="text-[10px] uppercase tracking-wider text-[#86868B] font-bold block mb-1">{label}</label>
    <input type="date" value={value} min={min} max={max} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className="w-full bg-[#F5F5F7] border border-gray-200 rounded-xl px-3 py-2.5 text-sm font-semibold text-[#1D1D1F]" />
  </div>
);

const FieldSelect = ({ label, value, onChange, options, testid, dispName }) => (
  <div>
    <label className="text-[10px] uppercase tracking-wider text-[#86868B] font-bold block mb-1">{label}</label>
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className="w-full bg-[#F5F5F7] border border-gray-200 rounded-xl px-3 py-2.5 text-sm font-semibold text-[#1D1D1F]">
      {options.map(o => <option key={o.id || "all"} value={o.id || ""}>{dispName ? dispName(o) : o.name}</option>)}
    </select>
  </div>
);

// Edit modal — Product / Boxes / Status / Delivery date / Salesman / Notes
const EditOrderModal = ({ order, products, salesmen, managers, onClose, onSaved, lang }) => {
  const allPersons = [...salesmen, ...managers];
  const [items, setItems] = useState(() => order.items?.map(i => ({ product_id: i.product_id, quantity: i.quantity, name: i.name_en || i.name })) || []);
  const [status, setStatus] = useState(order.status || "pending");
  const [deliveredAt, setDeliveredAt] = useState((order.delivered_at || "").slice(0, 10));
  const [salesmanId, setSalesmanId] = useState(order.assigned_salesman_id || "");
  const [notes, setNotes] = useState(order.notes || "");
  const [saving, setSaving] = useState(false);

  const addLine = () => setItems([...items, { product_id: products[0]?.id || "", quantity: 1, name: "" }]);
  const updateLine = (idx, key, val) => {
    const next = [...items]; next[idx] = { ...next[idx], [key]: val }; setItems(next);
  };
  const removeLine = (idx) => setItems(items.filter((_, i) => i !== idx));

  const submit = async () => {
    setSaving(true);
    try {
      const payload = {
        items: items.filter(i => i.product_id && i.quantity > 0).map(i => ({
          product_id: i.product_id, quantity: parseInt(i.quantity, 10) || 0, name: i.name || ""
        })),
        status,
        delivered_at: deliveredAt ? `${deliveredAt}T00:00:00+00:00` : null,
        assigned_salesman_id: salesmanId || null,
        notes: notes || null,
      };
      await api.put(`/admin/orders/${order.id}`, payload);
      toast.success(lang === "ta" ? "சேமிக்கப்பட்டது" : "Saved");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 grid place-items-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl max-w-xl w-full max-h-[90vh] overflow-y-auto soft-shadow-md fade-up" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white p-5 border-b border-gray-100 flex items-center justify-between z-10">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[#86868B] font-bold">{order.shop_name}</div>
            <h3 className="heading-font font-bold text-xl">{lang === "ta" ? "ஆர்டர் திருத்து" : "Edit Order"}</h3>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-gray-100" data-testid="edit-close-btn"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-4">
          {/* Items */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-[10px] uppercase tracking-wider text-[#86868B] font-bold">{lang === "ta" ? "பொருட்கள்" : "Items"}</label>
              <button onClick={addLine} className="text-[#0055FF] text-xs font-bold flex items-center gap-1" data-testid="edit-add-line">
                <Plus className="w-3 h-3" /> {lang === "ta" ? "சேர்" : "Add"}
              </button>
            </div>
            <div className="space-y-2">
              {items.map((it, idx) => (
                <div key={idx} className="flex gap-2 items-center" data-testid={`edit-item-${idx}`}>
                  <select value={it.product_id} onChange={(e) => updateLine(idx, "product_id", e.target.value)}
                    className="flex-1 bg-[#F5F5F7] rounded-xl px-3 py-2 text-sm font-semibold">
                    {products.map(p => <option key={p.id} value={p.id}>{(lang === "ta" && p.name_ta) ? p.name_ta : (p.name_en || p.name)}</option>)}
                  </select>
                  <input type="number" min="1" value={it.quantity} onChange={(e) => updateLine(idx, "quantity", e.target.value)}
                    className="w-24 bg-[#F5F5F7] rounded-xl px-3 py-2 text-sm font-semibold text-center" data-testid={`edit-qty-${idx}`} />
                  <button onClick={() => removeLine(idx)} className="p-2 rounded-lg hover:bg-red-50 text-[#FF3B30]"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
              {items.length === 0 && <div className="text-xs text-[#86868B] py-2 text-center">{lang === "ta" ? "பொருள் சேர்க்கவும்" : "Add at least one item"}</div>}
            </div>
          </div>

          {/* Status */}
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#86868B] font-bold block mb-1">{lang === "ta" ? "நிலை" : "Status"}</label>
            <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="edit-status"
              className="w-full bg-[#F5F5F7] rounded-xl px-3 py-2.5 text-sm font-semibold">
              {STATUS_KEYS.map(k => <option key={k} value={k}>{lang === "ta" ? STATUS_BADGES[k].ta : STATUS_BADGES[k].en}</option>)}
            </select>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#86868B] font-bold block mb-1">{lang === "ta" ? "டெலிவரி தேதி" : "Delivery Date"}</label>
              <input type="date" value={deliveredAt} onChange={(e) => setDeliveredAt(e.target.value)} data-testid="edit-delivered-at"
                className="w-full bg-[#F5F5F7] rounded-xl px-3 py-2.5 text-sm font-semibold" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#86868B] font-bold block mb-1">{lang === "ta" ? "சேல்ஸ்மேன்" : "Salesman"}</label>
              <select value={salesmanId} onChange={(e) => setSalesmanId(e.target.value)} data-testid="edit-salesman"
                className="w-full bg-[#F5F5F7] rounded-xl px-3 py-2.5 text-sm font-semibold">
                <option value="">{lang === "ta" ? "ஒதுக்கப்படாதது" : "Unassigned"}</option>
                {allPersons.map(s => <option key={s.id} value={s.id}>{(lang === "ta" && s.name_ta) ? s.name_ta : s.name} ({s.role})</option>)}
              </select>
            </div>
          </div>

          {/* Notes (optional) */}
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#86868B] font-bold block mb-1">{lang === "ta" ? "குறிப்புகள் (விருப்பம்)" : "Notes (optional)"}</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="edit-notes" rows="2"
              className="w-full bg-[#F5F5F7] rounded-xl px-3 py-2.5 text-sm font-semibold resize-none"
              placeholder={lang === "ta" ? "உள் குறிப்பு…" : "Internal note…"} />
          </div>

          <button onClick={submit} disabled={saving || items.length === 0} data-testid="edit-save-btn"
            className="w-full bg-[#0055FF] hover:bg-[#0044CC] disabled:opacity-50 text-white rounded-xl py-3 font-bold flex items-center justify-center gap-2">
            <Save className="w-4 h-4" /> {saving ? (lang === "ta" ? "சேமிக்கிறது…" : "Saving…") : (lang === "ta" ? "சேமி" : "Save")}
          </button>
        </div>
      </div>
    </div>
  );
};

// Edit modal for No-Sale (Not Bought) record
const EditNoSaleModal = ({ record, onClose, onSaved, lang }) => {
  const [shopName, setShopName] = useState(record.shop_name || "");
  const [phone, setPhone] = useState(record.phone || "");
  const [reasonCode, setReasonCode] = useState(record.reason_code || "other");
  const [customReason, setCustomReason] = useState(record.custom_reason || "");
  const [notes, setNotes] = useState(record.notes || "");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      const payload = {
        shop_name: shopName,
        phone: phone || null,
        reason_code: reasonCode,
        custom_reason: reasonCode === "other" ? (customReason || null) : null,
        notes: notes || null,
      };
      await api.put(`/admin/no-sales/${record.id}`, payload);
      toast.success(lang === "ta" ? "சேமிக்கப்பட்டது" : "Saved");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 grid place-items-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl max-w-xl w-full max-h-[90vh] overflow-y-auto soft-shadow-md fade-up" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white p-5 border-b border-gray-100 flex items-center justify-between z-10">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[#FF3B30] font-bold">{lang === "ta" ? "வாங்கல" : "Not Bought"}</div>
            <h3 className="heading-font font-bold text-xl">{lang === "ta" ? "பதிவு திருத்து" : "Edit Record"}</h3>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-gray-100" data-testid="nosale-edit-close-btn"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#86868B] font-bold block mb-1">{lang === "ta" ? "கடை பெயர்" : "Shop Name"}</label>
            <input value={shopName} onChange={(e) => setShopName(e.target.value)} data-testid="nosale-edit-shop"
              className="w-full bg-[#F5F5F7] rounded-xl px-3 py-2.5 text-sm font-semibold" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#86868B] font-bold block mb-1">{lang === "ta" ? "தொலைபேசி" : "Phone"}</label>
            <input value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="nosale-edit-phone"
              className="w-full bg-[#F5F5F7] rounded-xl px-3 py-2.5 text-sm font-semibold" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#86868B] font-bold block mb-1">{lang === "ta" ? "காரணம்" : "Reason"}</label>
            <select value={reasonCode} onChange={(e) => setReasonCode(e.target.value)} data-testid="nosale-edit-reason"
              className="w-full bg-[#F5F5F7] rounded-xl px-3 py-2.5 text-sm font-semibold">
              {REASON_KEYS.map(k => <option key={k} value={k}>{lang === "ta" ? REASON_LABELS[k].ta : REASON_LABELS[k].en}</option>)}
            </select>
          </div>
          {reasonCode === "other" && (
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#86868B] font-bold block mb-1">{lang === "ta" ? "தனிப்பட்ட காரணம்" : "Custom Reason"}</label>
              <input value={customReason} onChange={(e) => setCustomReason(e.target.value)} data-testid="nosale-edit-custom-reason"
                className="w-full bg-[#F5F5F7] rounded-xl px-3 py-2.5 text-sm font-semibold" />
            </div>
          )}
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#86868B] font-bold block mb-1">{lang === "ta" ? "குறிப்புகள் (விருப்பம்)" : "Notes (optional)"}</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="nosale-edit-notes" rows="2"
              className="w-full bg-[#F5F5F7] rounded-xl px-3 py-2.5 text-sm font-semibold resize-none" />
          </div>

          <button onClick={submit} disabled={saving || !shopName} data-testid="nosale-edit-save-btn"
            className="w-full bg-[#0055FF] hover:bg-[#0044CC] disabled:opacity-50 text-white rounded-xl py-3 font-bold flex items-center justify-center gap-2">
            <Save className="w-4 h-4" /> {saving ? (lang === "ta" ? "சேமிக்கிறது…" : "Saving…") : (lang === "ta" ? "சேமி" : "Save")}
          </button>
        </div>
      </div>
    </div>
  );
};
